import { reloadPage } from "./reload"

const CHUNK_RELOAD_FLAG = "carbon-bank:chunk-reloaded"

const CHUNK_ERROR_PATTERNS = [
	"Failed to fetch dynamically imported module",
	"error loading dynamically imported module",
	"Importing a module script failed",
]

const isChunkLoadError = (error: unknown): boolean =>
	error instanceof Error &&
	CHUNK_ERROR_PATTERNS.some(pattern =>
		error.message.toLowerCase().includes(pattern.toLowerCase()),
	)

const wait = (ms: number): Promise<void> =>
	new Promise(resolve => setTimeout(resolve, ms))

/**
 * Wraps a dynamic `import()` so that stale chunks after a redeploy no longer
 * crash the app. A user running an old `index.html` may request a hashed chunk
 * that no longer exists on the server; the import then rejects with
 * "Failed to fetch dynamically imported module".
 *
 * Strategy: retry the import (covers transient network blips). If it still
 * fails with a chunk-load error, force a single full page reload so the browser
 * fetches the fresh `index.html` and its new chunk hashes. A sessionStorage
 * flag prevents an infinite reload loop when the chunk is genuinely missing.
 */
export const retryDynamicImport = async <T>(
	importer: () => Promise<T>,
	retries = 1,
	interval = 500,
): Promise<T> => {
	try {
		const module = await importer()
		globalThis.sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
		return module
	} catch (error) {
		if (retries > 0) {
			await wait(interval)
			return retryDynamicImport(importer, retries - 1, interval)
		}

		const hasReloaded =
			globalThis.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === "true"

		if (isChunkLoadError(error) && !hasReloaded) {
			globalThis.sessionStorage.setItem(CHUNK_RELOAD_FLAG, "true")
			reloadPage()
			// Never resolve so the router does not render an error before reload.
			return new Promise<T>(() => {})
		}

		throw error
	}
}
